import {
  X,
  TrendingUp,
  Wallet,
  AlertTriangle,
  MessageCircle,
  RefreshCcw,
  AlertCircle,
} from "lucide-react";
import { FinanceOverviewItem } from "../../types/FinanceOverviewItem";
import { ProjectCheck } from "../../types/ProjectCheck";
import "./ProjectInfoComponent.css";
import logo from "../../assets/crmlogo.png";
import ClockodoLogo from "../ClockodoLogo/ClockodoLogo";
import { useProjects } from "../../customHooks/useProjects";
import { useState } from "react";

interface Props {
  projectCheck: ProjectCheck | FinanceOverviewItem;
  closeInfo: () => void;
}

function ProjectInfoComponent({ projectCheck, closeInfo }: Props) {
  const { refreshProject } = useProjects();

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [refreshError, setRefreshError] = useState<string | undefined>(undefined);

  const budget = Number(projectCheck.budget ?? 0);
  const revenue = Number(projectCheck.revenue ?? 0);

  const hasRevenue = revenue > 0;

  const hasBudget = budget > 0;

  const hasClockodoNote =
    projectCheck.clockodoNote !== undefined && projectCheck.clockodoNote.trim() !== "";

  const percentage = hasBudget ? Math.round((revenue / budget) * 100) : 0;

  const chartPercentage = hasBudget ? Math.min(percentage, 100) : 0;

  const isOverBudget = revenue > budget;

  const formattedDate = new Date(projectCheck.projectLastUpdatedAt).toLocaleString("de-CH", {
    timeZone: "Europe/Zurich",
    dateStyle: "short",
    timeStyle: "short",
  });

  const refreshProjectData = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshError(undefined);

    try {
      await refreshProject(projectCheck.crmProjectId);

      window.location.assign(`/finance?projectId=${projectCheck.crmProjectId}`);
    } catch {
      setRefreshError("Aktualisiertung fehlgeschlagen. Bitte versuche es erneut.");
      setIsRefreshing(false);
    }
  };

  return (
    <div className="info-overlay" onClick={closeInfo}>
      <div className="safe-area" onClick={(e) => e.stopPropagation()}>
        <div
          className={`info-content ${isRefreshing ? "is-refreshing" : ""}`}
          aria-busy={isRefreshing}
        >
          <div className="top-row-project-info">
            <div>
              <div className="project-info-title">{projectCheck.projectTitle}</div>

              <div className="project-number">#{projectCheck.projectNumber}</div>
            </div>

            <X onClick={closeInfo} className="close-button" />
          </div>

          <div className="project-subtitle-container">
            <div className="project-subtitle">{projectCheck.clientName}</div>
            <div className="refresh-area">
              {isRefreshing && <span className="refresh-status">Wird aktualisiert…</span>}
              <RefreshCcw
                className={`project-refresh ${isRefreshing ? "is-refreshing" : ""}`}
                onClick={refreshProjectData}
                aria-label="Projektdaten aktualisieren"
              />
            </div>
          </div>

          {refreshError && (
            <div className="error-box" role="alert">
              <AlertCircle size={18} />
              <span>{refreshError}</span>
              <X size={16} className="error-dismiss" onClick={() => setRefreshError(undefined)} />
            </div>
          )}

          <div className="project-meta">
            {"managerName" in projectCheck && (
              <div className="project-manager">Projektleiter: {projectCheck.managerName}</div>
            )}

            <div className="project-update-date">Datenstand: {formattedDate}</div>
          </div>
          <div className="project-main-content">
            <div className="project-left">
              {hasRevenue && (
                <div className="info-card">
                  <div className="info-label">
                    <TrendingUp size={16} />
                    Umsatz
                  </div>

                  <div className="info-value">
                    CHF{" "}
                    {revenue.toLocaleString("de-CH", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              )}
              {hasBudget && (
                <>
                  <div className="info-card">
                    <div className="info-label">
                      <Wallet size={16} />
                      Budget
                    </div>

                    <div className="info-value">
                      CHF{" "}
                      {budget.toLocaleString("de-CH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>

                  {isOverBudget && (
                    <div className="warning-box">
                      <AlertTriangle size={18} />
                      Der Umsatz hat das geplante Budget überschritten.
                    </div>
                  )}
                </>
              )}

              {hasClockodoNote && (
                <div className="info-card">
                  <div className="info-label">
                    <MessageCircle size={16} />
                    Clockodo Bemerkung
                  </div>
                  <div className="clockodo-note">{projectCheck.clockodoNote}</div>
                </div>
              )}

              <div className="project-links">
                <ClockodoLogo clockodoId={projectCheck.clockodoId} />

                {"crmProjectId" in projectCheck && (
                  <a
                    href={`https://ioz365.crm4.dynamics.com/main.aspx?appid=04584bc9-d9f6-e811-a957-000d3ab3f13b&pagetype=entityrecord&etn=ioz_customerproject&id=${projectCheck.crmProjectId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={logo} alt="CRM Logo" className="crm-logo" />
                  </a>
                )}
              </div>
            </div>

            {hasBudget && (
              <div className="project-right">
                <div className="chart-wrapper">
                  <div className="chart-circle">
                    <div
                      className="chart-progress"
                      style={{
                        background: `conic-gradient(
                        ${isOverBudget ? "#f97316" : "#3b82f6"} ${chartPercentage}%,
                        #e5e7eb ${chartPercentage}%
                        )`,
                      }}
                    >
                      <div className="chart-inner">
                        <span className="chart-number">{percentage}%</span>

                        <span className="chart-label">vom Budget</span>
                      </div>
                    </div>
                  </div>

                  <div className="chart-caption">
                    {isOverBudget ? "Budget überschritten" : "Budgetauslastung"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectInfoComponent;
